import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { cn } from '../../lib/utils';
import {
  Upload, ScanLine, FileText, Shield, Users, Calendar,
  CheckCircle2, Sparkles, Loader2, ArrowRight,
  Image, ClipboardList, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const DOC_TYPES = [
  { id: 'patient_id', label: 'Patient ID Card', icon: Users, desc: 'Extract name, DOB, address, photo', color: 'bg-blue-50 text-blue-600', credit: 1 },
  { id: 'insurance', label: 'Insurance Card', icon: Shield, desc: 'Extract plan, member ID, group, copay', color: 'bg-emerald-50 text-emerald-600', credit: 1 },
  { id: 'referral', label: 'Referral Document', icon: FileText, desc: 'Extract referring provider, diagnosis, auth', color: 'bg-violet-50 text-violet-600', credit: 1 },
  { id: 'schedule', label: 'Patient Schedule', icon: Calendar, desc: 'Extract appointments, dates, providers', color: 'bg-amber-50 text-amber-600', credit: 2 },
  { id: 'fax', label: 'Fax / PDF', icon: ClipboardList, desc: 'Extract structured data from faxes', color: 'bg-slate-100 text-slate-600', credit: 1 },
  { id: 'screenshot', label: 'Screenshot / Image', icon: Image, desc: 'OCR any image for text extraction', color: 'bg-pink-50 text-pink-600', credit: 1 },
];

const EXTRACTED = {
  patient_id: { type: 'Patient ID', data: [
    { label: 'Full Name', value: 'Amanda L. Rodriguez', confidence: 99 },
    { label: 'Date of Birth', value: '05/14/1988', confidence: 98 },
    { label: 'Address', value: '456 Oak Avenue, Springfield, IL 62702', confidence: 95 },
    { label: 'ID Number', value: 'IL-DL-892341', confidence: 97 },
    { label: 'Gender', value: 'Female', confidence: 99 },
  ], actions: ['Create Patient Profile', 'Schedule Appointment', 'Update Existing Patient'] },
  insurance: { type: 'Insurance Card', data: [
    { label: 'Insurance Plan', value: 'Blue Cross Blue Shield PPO', confidence: 99 },
    { label: 'Member ID', value: 'BCB-987654321', confidence: 98 },
    { label: 'Group Number', value: 'GRP-5432', confidence: 97 },
    { label: 'Copay', value: '$30 Office Visit / $50 Specialist', confidence: 94 },
    { label: 'Effective Date', value: '01/01/2026', confidence: 96 },
    { label: 'Phone', value: '(800) 123-4567', confidence: 99 },
  ], actions: ['Update Insurance Info', 'Verify Eligibility', 'Schedule Appointment'] },
  referral: { type: 'Referral Document', data: [
    { label: 'Referring Provider', value: 'Dr. Sarah Mitchell, MD', confidence: 97 },
    { label: 'Referring Practice', value: 'Springfield Primary Care', confidence: 96 },
    { label: 'Patient', value: 'John Smith', confidence: 99 },
    { label: 'Diagnosis', value: 'E11.9 - Type 2 Diabetes Mellitus', confidence: 95 },
    { label: 'Referral Reason', value: 'Endocrinology consult for A1c management', confidence: 92 },
    { label: 'Authorization #', value: 'AUTH-2026-8821', confidence: 98 },
    { label: 'Valid Through', value: '06/30/2026', confidence: 97 },
  ], actions: ['Create Appointment', 'Link to Patient'] },
  schedule: { type: 'Patient Schedule', data: [
    { label: 'Document Type', value: 'Multi-Patient Schedule', confidence: 96 },
    { label: 'Date Range', value: '03/17/2026 - 03/21/2026', confidence: 98 },
    { label: 'Patients Found', value: '24 appointments extracted', confidence: 94 },
    { label: 'Providers', value: 'Dr. Chen, Dr. Wilson, Dr. Mitchell', confidence: 97 },
    { label: 'Conflicts', value: '2 potential double-bookings detected', confidence: 91 },
  ], actions: ['Import to Calendar', 'Review Conflicts'] },
  fax: { type: 'Fax Document', data: [
    { label: 'Sender', value: 'Springfield General Hospital', confidence: 95 },
    { label: 'Pages', value: '3 pages processed', confidence: 99 },
    { label: 'Content', value: 'Discharge summary for Maria Garcia', confidence: 93 },
    { label: 'Key Findings', value: 'Follow-up in 7 days, new medications prescribed', confidence: 90 },
  ], actions: ['Route to Provider', 'Link to Patient'] },
  screenshot: { type: 'Screenshot/Image', data: [
    { label: 'Text Extracted', value: 'Insurance eligibility confirmation', confidence: 92 },
    { label: 'Key Info', value: 'Coverage active, pre-auth not required for office visit', confidence: 88 },
  ], actions: ['Save to Patient File', 'Share with Team'] },
};

const EchoLens = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleData, setScheduleData] = useState({ type: '', date: '', time: '', provider: '' });

  const handleUpload = (type) => {
    setSelectedType(type);
    setUploadedFile(`${type}_document.pdf`);
    setProcessing(true);
    setResult(null);
    setShowSchedule(false);
    setTimeout(() => { setProcessing(false); setResult(EXTRACTED[type]); }, 2500);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && selectedType) {
      setUploadedFile(file.name);
      setProcessing(true);
      setResult(null);
      setTimeout(() => { setProcessing(false); setResult(EXTRACTED[selectedType]); }, 2500);
    }
  };

  const handleAction = (action) => {
    if (action === 'Schedule Appointment' || action === 'Create Appointment') {
      setShowSchedule(true);
      return;
    }
    toast.success(action, { description: 'Data has been processed successfully.' });
    if (action.includes('Patient') || action.includes('Update') || action.includes('Link')) navigate('/echo/patients');
    else if (action.includes('Calendar')) navigate('/echo/schedule');
  };

  const handleScheduleConfirm = () => {
    if (!scheduleData.date || !scheduleData.time || !scheduleData.provider) { toast.error('Please fill all scheduling fields'); return; }
    toast.success('Appointment scheduled', { description: `${scheduleData.date} at ${scheduleData.time} with ${scheduleData.provider}` });
    setShowSchedule(false);
    navigate('/echo/schedule');
  };

  const reset = () => { setSelectedType(null); setResult(null); setUploadedFile(null); setProcessing(false); setShowSchedule(false); };

  const selectedDoc = DOC_TYPES.find(d => d.id === selectedType);

  return (
    <div className="space-y-6" data-testid="echo-lens-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center"><ScanLine className="h-5 w-5 text-violet-600" /></div>
          <div>
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Lens</h1>
            <p className="text-slate-500 mt-0.5">Upload documents and images — AI extracts structured data automatically</p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5"><Coins className="h-3 w-3 text-amber-500" /><span className="text-xs">1 credit per scan</span></Badge>
      </div>

      {!selectedType && !processing && !result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOC_TYPES.map((doc) => (
              <Card key={doc.id} className="border-slate-200 hover:border-slate-300 cursor-pointer transition-all group" onClick={() => handleUpload(doc.id)} data-testid={`doc-type-${doc.id}`}>
                <CardContent className="p-5">
                  <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center mb-3", doc.color.split(' ')[0])}>
                    <doc.icon className={cn("h-5 w-5", doc.color.split(' ')[1])} />
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">{doc.label}</p>
                  <p className="text-xs text-muted-foreground">{doc.desc}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-slate-600 transition-colors"><ScanLine className="h-3 w-3" /> Click to upload or try demo</div>
                    <Badge variant="outline" className="text-[9px] gap-0.5 ml-auto"><Coins className="h-2.5 w-2.5 text-amber-500" />{doc.credit} credit{doc.credit > 1 ? 's' : ''}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center"><Upload className="h-5 w-5 text-slate-500" /></div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Batch Upload</p>
                    <p className="text-xs text-muted-foreground">Upload multiple documents at once. AI auto-classifies and extracts from each.</p>
                  </div>
                </div>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" multiple accept="image/*,.pdf" onChange={() => toast.success('Batch processing started', { description: '3 documents queued for extraction.' })} />
                  <Button variant="outline" asChild className="gap-2" data-testid="batch-upload-btn"><span><Upload className="h-4 w-4" /> Upload Batch</span></Button>
                </label>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {(selectedType || processing || result) && (
        <div className="max-w-2xl mx-auto space-y-4">
          <Button variant="ghost" onClick={reset} className="gap-2 text-sm" data-testid="lens-back-btn"><ArrowRight className="h-4 w-4 rotate-180" /> Back to document types</Button>

          {!result && !processing && (
            <Card className="border-dashed border-2 border-slate-300">
              <CardContent className="p-8 text-center">
                <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700 mb-1">Upload {selectedDoc?.label}</p>
                <p className="text-xs text-muted-foreground mb-4">Drag & drop or click to select. Supports PDF, PNG, JPG.</p>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileChange} />
                  <Button asChild className="bg-black hover:bg-gray-800 gap-2"><span><Upload className="h-4 w-4" /> Choose File</span></Button>
                </label>
              </CardContent>
            </Card>
          )}

          {processing && (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center space-y-3">
                <Loader2 className="h-8 w-8 text-slate-400 mx-auto animate-spin" />
                <p className="text-sm font-medium text-slate-700">AI is extracting data...</p>
                <p className="text-xs text-muted-foreground">Analyzing {uploadedFile || 'document'}</p>
                <Badge className="bg-violet-100 text-violet-700 text-[10px] gap-1"><Coins className="h-3 w-3" />{selectedDoc?.credit || 1} credit</Badge>
              </CardContent>
            </Card>
          )}

          {result && !showSchedule && (
            <Card className="border-slate-200" data-testid="lens-result">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Extraction Complete</CardTitle>
                    <CardDescription>{result.type} — {result.data.length} fields extracted</CardDescription>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] gap-1"><Coins className="h-3 w-3" />{selectedDoc?.credit || 1} credit used</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {result.data.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200" data-testid={`extracted-field-${i}`}>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{d.label}</p>
                      <p className="text-sm font-medium text-slate-800">{d.value}</p>
                    </div>
                    <Badge variant="outline" className={cn("text-[10px]", d.confidence >= 95 ? 'border-emerald-300 text-emerald-600' : d.confidence >= 90 ? 'border-amber-300 text-amber-600' : 'border-red-300 text-red-600')}>{d.confidence}%</Badge>
                  </div>
                ))}
                <div className="flex gap-2 pt-2 flex-wrap">
                  {result.actions.map((action, i) => (
                    <Button key={i} onClick={() => handleAction(action)} className={i === 0 ? "bg-black hover:bg-gray-800 gap-2 flex-1" : "flex-1 gap-2"} variant={i === 0 ? "default" : "outline"} data-testid={`action-${i}`}>
                      {action}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inline Scheduling (replaces AI Scheduling Wizard) */}
          {showSchedule && (
            <Card className="border-slate-200" data-testid="lens-schedule-form">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Schedule Appointment</CardTitle>
                <CardDescription>Schedule directly from extracted data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-xs font-medium text-slate-700 mb-1">Patient (from extraction)</p>
                  <p className="text-sm text-slate-800">{result?.data?.[0]?.value || 'Amanda L. Rodriguez'}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Appointment Type</Label>
                    <Select value={scheduleData.type} onValueChange={v => setScheduleData(p => ({ ...p, type: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent><SelectItem value="new_patient">New Patient</SelectItem><SelectItem value="follow_up">Follow-up</SelectItem><SelectItem value="sick_visit">Sick Visit</SelectItem><SelectItem value="wellness">Wellness</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Provider</Label>
                    <Select value={scheduleData.provider} onValueChange={v => setScheduleData(p => ({ ...p, provider: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{isDemo && (<>
                        <SelectItem value="Dr. Chen">Dr. Chen</SelectItem>
                        <SelectItem value="Dr. Wilson">Dr. Wilson</SelectItem>
                        <SelectItem value="Dr. Mitchell">Dr. Mitchell</SelectItem>
                      </>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={scheduleData.date} onChange={e => setScheduleData(p => ({ ...p, date: e.target.value }))} /></div>
                  <div><Label>Time</Label>
                    <Select value={scheduleData.time} onValueChange={v => setScheduleData(p => ({ ...p, time: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{['9:00 AM','9:30 AM','10:00 AM','10:30 AM','11:00 AM','1:00 PM','1:30 PM','2:00 PM','2:30 PM','3:00 PM'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowSchedule(false)} className="flex-1">Back to Results</Button>
                  <Button className="bg-black hover:bg-gray-800 flex-1 gap-2" onClick={handleScheduleConfirm} data-testid="confirm-schedule-btn"><Calendar className="h-4 w-4" /> Confirm & Schedule</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default EchoLens;
